import { Input, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Countries } from '../../library/constants';
import { getFieldErrors } from '../../library/helpers/forms';
import { useValidationForm } from '../../providers/form';
import { ValidationForm as Form } from '../form';
import styles from './PhoneInput.module.scss';
import './PhoneInput.scss';

function PhoneInput({ placeholder, valuePropName = { code: 'code', number: 'number' }, label = "Contact", dependencies = [], required, disabled, readOnly = false }) {
    const form = Form.useFormInstance();
    const { errors, disabled: formDisabled = false, readOnly: formReadOnly = false } = useValidationForm();
    const country_code = Form.useWatch(valuePropName.code, form) ?? form.getFieldValue(valuePropName.code);
    const isDisabled = disabled ?? formDisabled;
    const isReadOnly = isDisabled || readOnly || formReadOnly;

    const normalizeValue = (value = '') => {
        const filteredValue = String(value).replace(/[^0-9]/g, '');
        return filteredValue;
    };

    const validationRules = useMemo(() => {
        const rules = [{ min: 6, message: `${label} is too short` }]
        if (required) {
            rules.push({ required: true, message: `${label} is required` })
        }
        return rules
    }, [label, required])

    async function countryCodeValidator(_, value) {
        if ((required || !!form.getFieldValue(valuePropName.number)) && !value?.length) {
            return Promise.reject('Country code is required')
        }
        return Promise.resolve()
    }

    const fieldErrors = () => {
        const codeErrors = getFieldErrors(valuePropName.code, errors)
        const numberErrors = getFieldErrors(valuePropName.number, errors)

        if (required) {
            return numberErrors?.length ? numberErrors : codeErrors?.length ? codeErrors : null;
        }

        // For non-required fields, return null if neither error exists
        if (!numberErrors?.length && !codeErrors?.length) {
            return null;
        }

        // Return numberErrors if they exist, otherwise return codeErrors
        return numberErrors?.length ? numberErrors : codeErrors;
    }

    return (
        <span className={`${styles.wrapper} ${isReadOnly ? styles.readOnly : ''} phone-input`}>
            <div className={`${styles.innerWrapper} ant-input`}>
                <Form.Item name={valuePropName.code} rules={[{ validator: countryCodeValidator }]} dependencies={[valuePropName.number]} noStyle>
                    <Select
                        optionLabelProp="title"
                        dropdownMatchSelectWidth={false}
                        autoComplete="none"
                        className={styles.countryCode}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={
                            Countries.map(({ dial_code, code, name, emoji }) => ({
                                label: [emoji, name, '+' + dial_code].join(' '),
                                value: dial_code,
                                key: dial_code + code,
                                title: emoji,
                            }))}
                        showSearch
                        variant="borderless"
                        disabled={isDisabled || readOnly || formReadOnly}
                    />
                </Form.Item>
                <Form.Item name={valuePropName.number} rules={validationRules} getValueFromEvent={(event) => normalizeValue(event.target.value)} dependencies={[...dependencies, valuePropName.code]} noStyle>
                    <Input
                        maxLength={15}
                        className={styles.phone}
                        prefix={<>{country_code ? "+ " + country_code : ''}</>}
                        onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                            }
                        }}
                        placeholder={placeholder}
                        variant="borderless"
                        disabled={isDisabled}
                        readOnly={readOnly || formReadOnly}
                    />
                </Form.Item>
            </div>
            <div className="ant-form-item-explain" role="alert">
                {fieldErrors()?.map((error, i) => (
                    <div key={i}><span className="error">{error}</span></div>
                ))}
            </div>
        </span>
    );
}

PhoneInput.propTypes = {
    placeholder: PropTypes.string,
    valuePropName: PropTypes.exact({
        code: PropTypes.string,
        number: PropTypes.string,
    }),
    dependencies: PropTypes.arrayOf(PropTypes.string),
    required: PropTypes.bool,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
}

export default PhoneInput;
